import { TrackingEvent } from '../tracking/types';
import {
  FlutterInappwebviewHandler,
  GetInboxMessagesEvent,
  MarkMessageOpenedEvent,
  PublishEventData,
  TransactionInitEvent,
} from './types';

/**
 * NativeEventBus is a singleton class that is used to provide a bridge between the web and native sides.
 * It can be used to track analytics events produced by the client on web side.
 */
export class NativeEventBus {
  private static instance: NativeEventBus;
  private static androidUserAgentPostfix = 'BetzoneAndroid/v25';
  private static iosUserAgentPostfix = 'BetzoneiOS/v25';
  private static unknownUserAgentPostfix = 'UnknownPlatform/v25';
  private static itvWinUserAgentPostfix = 'ITVWin';
  private static userAgents = [
    this.androidUserAgentPostfix,
    this.iosUserAgentPostfix,
    this.unknownUserAgentPostfix,
    this.itvWinUserAgentPostfix,
  ];

  constructor() {
    if (NativeEventBus.instance) {
      return NativeEventBus.instance;
    }

    NativeEventBus.instance = this;
  }

  public static isNativeBridgeAvailable(): boolean {
    const flag = this.userAgents.some((userAgent) => window.navigator.userAgent.includes(userAgent));

    return flag;
  }

  /**
   * Track an analytics event on the native side.
   * @param data - The event data to track.
   */
  public trackEvent(data: TrackingEvent): void {
    this.publishEventIfAvailable(FlutterInappwebviewHandler.TRACK_EVENT, data);
  }

  public closeCashier(): void {
    this.publishEventIfAvailable(FlutterInappwebviewHandler.CLOSE_CASHIER);
  }

  public transactionInit(data: TransactionInitEvent): void {
    this.publishEventIfAvailable(FlutterInappwebviewHandler.TRANSACTION_INIT, data);
  }

  public getInboxMessages(data?: GetInboxMessagesEvent): void {
    this.publishEventIfAvailable(FlutterInappwebviewHandler.GET_INBOX_MESSAGES, data);
  }

  public getInboxBadge(): void {
    this.publishEventIfAvailable(FlutterInappwebviewHandler.GET_INBOX_BADGE);
  }

  public markMessageOpened(data: MarkMessageOpenedEvent): void {
    this.publishEventIfAvailable(FlutterInappwebviewHandler.MARK_MESSAGE_OPENED, data);
  }

  public listenToEventBeforeInitialization(type: string, callback: EventListener): void {
    window.addEventListener(`native-${type}`, callback as EventListener);
  }

  public listenToEvent(type: string, callback: (event: MessageEvent) => void): void {
    this.establishConnection('addEventListener', type, this.callbackWrapper(callback));
  }

  public removeListener(type: string, callback: (event: MessageEvent) => void): void {
    this.establishConnection('removeEventListener', type, this.callbackWrapper(callback));
  }

  private callbackWrapper(callback: (event: MessageEvent) => void): (event: Event) => void {
    return (event: Event) => {
      const messageEvent = event as MessageEvent;

      callback(messageEvent);
    };
  }

  private establishConnection(
    action: 'addEventListener' | 'removeEventListener',
    type: string,
    callback: EventListener
  ): void {
    if (NativeEventBus.isNativeBridgeAvailable()) {
      window[action](`native-${type}`, callback);
    }
  }

  private isItvWin(): boolean {
    return window.navigator.userAgent.includes(NativeEventBus.itvWinUserAgentPostfix);
  }

  private publishEventIfAvailable(handler: FlutterInappwebviewHandler, data?: PublishEventData): void {
    if (NativeEventBus.isNativeBridgeAvailable()) {
      if (this.isItvWin() && window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ handler, data }));
      } else if (window.flutter_inappwebview) {
        window.flutter_inappwebview.callHandler(handler, data ?? null);
      }
    }
  }
}
